import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import i18next from 'i18next';
import _ from 'lodash';
import moment from 'moment';
import { isInsightBoardAvailable } from '@/actionBoards/insightBoardAvailability.utilities';
import {
  sqHomeScreenStore,
  sqLicenseManagementStore,
  sqWorkbenchStore,
  sqWorkbookStore,
  sqWorksheetStore,
} from '@/core/core.stores';
import { flux } from '@/core/flux.module';
import { PUSH_WORKBENCH } from '@/core/flux.service';
import { isSystemTest } from '@/core/utilities';
import { formatTime } from '@/datetime/dateTime.utilities';
import { getPreferredTimeFormat } from '@/datetime/dateTimeFormatting.utilities';
import {
  HOME_SCREEN_CANCELLATION_GROUP,
  HOME_SCREEN_SORT,
  HOME_SCREEN_TABLE_TYPE,
  type SortByTableItem,
} from '@/homescreen/homescreen.constants';
import { loadSearchTable } from '@/homescreen/legacy/legacyHomeScreen.actions';
import { getMeilisearchResults } from '@/homescreen/search/omniSearch.service';
import { DEFAULT_SEARCH_CONFIG } from '@/homescreen/search/omniSearch.utilities';
import { Feature } from '@/licenseManagement/licenseManagement.constants';
import { hasValidFeatureInLicense } from '@/licenseManagement/licenseManagement.utilities';
import { HOME_SCREEN_TABS, SEARCH_ITEM_LOCATIONS, USERS_FOLDER_NAME } from '@/main/app.constants';
import { getLoginLink, goTo, returnToPreviousState } from '@/main/routing.utilities';
import { cancelAll, cancelGroup } from '@/requests/pendingRequests.utilities';
import { canModifyWorkbook } from '@/services/authorization.service';
import { AUTH_CHANGE_BROADCAST_CHANNEL, subscribeToBroadcastChannel } from '@/services/broadcastChannel.utilities';
import { emitWorkbooks } from '@/services/notifier.service';
import {
  isNewHomeScreen2025Enabled,
  isNewHomeScreenWithMeilisearchEnabled,
  isVantageEnabled,
} from '@/services/systemConfiguration.utilities';
import { isCsrfSet } from '@/utilities/auth.utilities';
import {
  createFolder,
  createProject,
  createWorkbook,
  getDefaultFolderName,
  getFolder,
  getFolderRoot,
  getTabFolder,
  getTabFolderName,
  getWorkbook,
  setArchived,
} from '@/utilities/homescreen.utilities';
import { equalsIgnoreCase } from '@/utilities/utilities';
import { setJournalToOpen } from '@/workbench/workbench.actions';
import { addWorksheetToWorkbook as addWorksheetToWorkbookAction, loadWorkbook } from '@/workbook/workbook.actions';
import { WORKBOOK_DISPLAY } from '@/workbook/workbook.constants';
import {
  GetFoldersFilterEnum as FilterEnum,
  GetFoldersSearchFieldsEnum as SearchFieldsEnum,
  sqFoldersApi,
} from '@seeqdev/api/endpoint/FoldersApi';
import type { HomeScreenItemType } from '@/homescreen/HomeScreenTable.organism';
import type { TrainingPromptDisplay } from '@/homescreen/homescreen.constants';
import type { HomeScreenSearchParams } from '@/homescreen/homescreen.store';
import type { SearchItemLocation } from '@/main/app.constants';
import type { FolderNavigationOutputV1 } from '@seeqdev/api/model/FolderNavigationOutputV1';

type GetSubfoldersParams = Parameters<typeof sqFoldersApi.getSubfolders>[0];
type GetSubfoldersConfig = Parameters<typeof sqFoldersApi.getSubfolders>[1];

const SEARCH_LOCATION_TO_FILTER: Partial<Record<SearchItemLocation, FilterEnum>> = {
  [SEARCH_ITEM_LOCATIONS.ALL]: FilterEnum.All,
  [SEARCH_ITEM_LOCATIONS.USERS]: FilterEnum.Users,
  [SEARCH_ITEM_LOCATIONS.CORPORATE]: FilterEnum.Corporate,
  [SEARCH_ITEM_LOCATIONS.MY_FOLDER]: FilterEnum.Mine,
  [SEARCH_ITEM_LOCATIONS.SHARED_OR_PUBLIC]: FilterEnum.SharedOrPublic,
};

const SEARCH_FIELD_TO_ENUM: Record<string, SearchFieldsEnum | undefined> = {
  all: SearchFieldsEnum.All,
  name: SearchFieldsEnum.Name,
  description: SearchFieldsEnum.Description,
  document: SearchFieldsEnum.Document,
};

const isFilterEnumValue = (filter: unknown): filter is FilterEnum =>
  Object.values(FilterEnum).includes(filter as FilterEnum);

function getNavigationSubfolders(params: GetSubfoldersParams, config?: GetSubfoldersConfig) {
  if (
    params.folderId === SEARCH_ITEM_LOCATIONS.SHARED_OR_PUBLIC ||
    params.root === SEARCH_ITEM_LOCATIONS.SHARED_OR_PUBLIC
  ) {
    // getSimpleSubFolders does not support SHARED_OR_PUBLIC or performance reasons
    return sqFoldersApi.getSubfolders(params, config);
  }

  return sqFoldersApi.getSimpleSubfolders({ folderId: params.folderId }, config);
}

export const normalizeFilter = (filter?: HomeScreenSearchParams['filter']): FilterEnum | undefined => {
  if (!filter) {
    return undefined;
  }

  if (isFilterEnumValue(filter)) {
    return filter;
  }

  return SEARCH_LOCATION_TO_FILTER[filter];
};

export const normalizeTypes = (types?: HomeScreenSearchParams['types']): string[] | undefined => {
  if (!types) {
    return undefined;
  }

  if (types.length === 0) {
    return [];
  }

  const first = types[0];
  return Array.isArray(first) ? (types as string[][]).flat() : (types as string[]);
};

const normalizeSearchFields = (
  searchFields?: HomeScreenSearchParams['searchFields'],
): SearchFieldsEnum[] | undefined => {
  if (!searchFields) {
    return undefined;
  }

  const fields = Array.isArray(searchFields) ? searchFields : [searchFields];
  const mapped = fields
    .map((field) => SEARCH_FIELD_TO_ENUM[field as keyof typeof SEARCH_FIELD_TO_ENUM])
    .filter((field): field is SearchFieldsEnum => field !== undefined);

  return mapped.length > 0 ? mapped : undefined;
};

export function setCurrentTab(tab: string) {
  flux.dispatch('SET_CURRENT_HOME_SCREEN_TAB', { tab }, PUSH_WORKBENCH);
}

export function setPreviousTab(tab: string) {
  flux.dispatch('SET_PREVIOUS_HOME_SCREEN_TAB', { tab }, PUSH_WORKBENCH);
}

export function setCurrentFolder(folderId: string | null | undefined) {
  flux.dispatch('SET_CURRENT_HOME_SCREEN_FOLDER', { folderId }, PUSH_WORKBENCH);
}

export function addExpandedFolderIds(expandedFolderIds: string[]) {
  flux.dispatch('ADD_EXPANDED_FOLDER_IDS', { expandedFolderIds });
}

export function removeExpandedFolderIds(collapsedFolderIds: string[]) {
  flux.dispatch('REMOVE_EXPANDED_FOLDER_IDS', { collapsedFolderIds });
}

export function mergeSubfolderTree(tree: FolderNavigationOutputV1[]) {
  flux.dispatch('MERGE_HOME_SCREEN_FOLDER_TREE', { tree });
}

export function replaceFolderChildren(folderId: string, subfolders: FolderNavigationOutputV1[]) {
  flux.dispatch('REPLACE_HOME_SCREEN_FOLDER_CHILDREN', { folderId, subfolders });
}

export function setTableSort(sortProperty: string, sortAsc: boolean, table: string) {
  flux.dispatch('SET_HOME_SCREEN_TABLE_SORT', { sortProperty, sortAsc, table }, PUSH_WORKBENCH);
}

export function clearBreadcrumbs() {
  flux.dispatch('CLEAR_BREADCRUMBS');
}

export function clearItemsHomescreen() {
  flux.dispatch('CLEAR_HOME_SCREEN_ITEMS');
}

export function setPageSizeForTable({ table, size }: { table?: string; size: number }) {
  flux.dispatch('SET_PAGE_SIZE_FOR_TABLE', { table, size }, PUSH_WORKBENCH);
}

export function setSearchParams(searchParams: HomeScreenSearchParams) {
  flux.dispatch('SET_HOME_SCREEN_SEARCH_PARAMS', { searchParams });
}

export function setIsExact(isExact: boolean) {
  flux.dispatch('SET_HOME_SCREEN_SEARCH_IS_EXACT', { isExact });
}

export function setPageNumber(pageNumber: number, table: string) {
  flux.dispatch('SET_HOME_SCREEN_PAGE_NUMBER', { pageNumber, table });
}

export function getCancellationGroupForTable(table: string) {
  return `${HOME_SCREEN_CANCELLATION_GROUP}_${table}`;
}

export function setPageNumberAndGo(
  pageNumber: number,
  loadTable: (...args: unknown[]) => unknown,
  table?: string,
  searchParams?: HomeScreenSearchParams,
) {
  setPageNumber(pageNumber, table as string);
  loadTable(HOME_SCREEN_TABLE_TYPE.SEARCH ? searchParams : null);
}

/**
 * Copies active search filters from the store into the given searchParams.
 * Content-type filter is always preserved. Owner/creator filters are only preserved
 * when `preserveOwnerCreator` is true (i.e. the backend supports them for this table).
 */
function getCurrentSearchParams(
  searchParams: HomeScreenSearchParams,
  { preserveOwnerCreator = true }: { preserveOwnerCreator?: boolean } = {},
): HomeScreenSearchParams {
  const { types, ownerIds, creatorIds } = sqHomeScreenStore.searchParams;
  let currentSearchParams = searchParams;

  if (types) currentSearchParams.types = types;

  if (preserveOwnerCreator) {
    if (ownerIds) currentSearchParams.ownerIds = ownerIds;
    if (creatorIds) currentSearchParams.creatorIds = creatorIds;
  }

  return currentSearchParams;
}

export function buildHomescreenSortOrder(
  sortProperty: string,
  sortAsc: boolean,
  { openedAtTiebreaker = false }: { openedAtTiebreaker?: boolean } = {},
): string {
  const direction = sortAsc ? 'asc' : 'desc';
  const primarySort = `${sortProperty} ${direction}`;

  if (openedAtTiebreaker && sortProperty !== HOME_SCREEN_SORT.OPENED_AT) {
    return `${primarySort}, ${HOME_SCREEN_SORT.OPENED_AT} ${direction}`;
  }

  return primarySort;
}

function getHomescreenSortOrder(
  sort?: SortByTableItem,
  options?: { openedAtTiebreaker?: boolean },
): string | undefined {
  if (!sort?.sortProperty) {
    return undefined;
  }

  return buildHomescreenSortOrder(sort.sortProperty, sort.sortAsc ?? true, options);
}

export function loadPinnedTable() {
  const table = HOME_SCREEN_TABLE_TYPE.PINNED;
  const sort = sqHomeScreenStore.getSortForTable(table);
  const searchParams: HomeScreenSearchParams = getCurrentSearchParams({
    sortOrder: getHomescreenSortOrder(sort),
    onlyPinned: true,
  });

  return loadTable({ searchParams, table, cancellationGroup: getCancellationGroupForTable('pinned') });
}

export function loadRecentTable() {
  const table = HOME_SCREEN_TABLE_TYPE.RECENT;
  const sort = sqHomeScreenStore.getSortForTable(table);
  const sortOrder = sort?.sortProperty
    ? getHomescreenSortOrder(sort, {
        openedAtTiebreaker: isNewHomeScreen2025Enabled(),
      })
    : buildHomescreenSortOrder(HOME_SCREEN_SORT.OPENED_AT, false);
  const searchParams: HomeScreenSearchParams = getCurrentSearchParams({ sortOrder });

  return loadTable({ searchParams, table, cancellationGroup: getCancellationGroupForTable('recent') });
}

type ResultEndpoint = 'meili' | 'folders' | 'navigate';

function selectEndpoint(
  table: string,
  searchParams: { filter?: string; folderId?: string; isArchived?: boolean },
): ResultEndpoint {
  const meiliEnabled = isNewHomeScreenWithMeilisearchEnabled();
  const isSearch = table === HOME_SCREEN_TABLE_TYPE.SEARCH;
  const isTab = table === HOME_SCREEN_TABLE_TYPE.TAB;
  const isSharedTab = isTab && searchParams.filter === SEARCH_ITEM_LOCATIONS.SHARED_OR_PUBLIC;
  const isSharedRoot = isSharedTab && !searchParams.folderId;
  const isArchivedTab = isTab && Boolean(searchParams.isArchived);

  // 1) Search
  if (isSearch) return meiliEnabled ? 'meili' : 'folders';

  // 2) Shared tab (all depths): never navigateFolders
  // root can use meili, subfolders use folders
  if (isSharedTab) {
    if (meiliEnabled && isSharedRoot) return 'meili';
    return 'folders';
  }

  // 3) Archived tab uses folders semantics
  if (isArchivedTab) return 'folders';

  // 4) Default tab navigation
  return 'navigate';
}

export function loadTabTable(
  options: {
    currentTab?: HOME_SCREEN_TABS;
    folderId?: string | null | undefined;
  } = {},
) {
  const table = HOME_SCREEN_TABLE_TYPE.TAB;
  const sort = sqHomeScreenStore.getSortForTable(table);
  const currentTab = options.currentTab ?? sqHomeScreenStore.currentTab;
  const folderId = options.folderId ?? sqHomeScreenStore.currentFolderId;
  const initialSearchParams = getSearchParams({
    currentTab,
    folderId: folderId as string,
    sortProperty: sort?.sortProperty,
    sortAsc: sort?.sortAsc,
  });
  const searchParams = getCurrentSearchParams(initialSearchParams, {
    preserveOwnerCreator: isNewHomeScreenWithMeilisearchEnabled(),
  });

  return loadTable({
    searchParams,
    table,
    currentTab,
    cancellationGroup: getCancellationGroupForTable('tab'),
  });
}

export async function loadTable({
  searchParams,
  table,
  currentTab,
  isCurrentFolderNested = false,
  advancedSearchSelectedFolder = '',
  locationChangedInAdvanced = false,
  breadcrumbClicked = false,
  cancellationGroup,
}: {
  searchParams: HomeScreenSearchParams;
  table: string;
  currentTab?: HOME_SCREEN_TABS;
  isCurrentFolderNested?: boolean;
  advancedSearchSelectedFolder?: string;
  locationChangedInAdvanced?: boolean;
  breadcrumbClicked?: boolean;
  cancellationGroup: string;
}) {
  flux.dispatch('SET_HOME_SCREEN_TABLE_LOADING', { table });
  flux.dispatch('SET_IS_LOADING_BREADCRUMBS', { isLoading: true });

  const limit = sqHomeScreenStore.getPageSizeByTable(table);
  const currentPageNumber = sqHomeScreenStore.getPageNumberForTable(table);
  const folderId =
    searchParams.folderId ||
    (!isCurrentFolderNested && advancedSearchSelectedFolder !== '') ||
    (advancedSearchSelectedFolder === '' && (locationChangedInAdvanced || breadcrumbClicked))
      ? searchParams.folderId
      : sqHomeScreenStore.currentFolderId;

  const getFolders = async (limit: number, pageNumber: number) => {
    let filter = searchParams.filter;
    if (folderId) {
      await getFolder(folderId).then((folder) => {
        if (folder?.name === USERS_FOLDER_NAME) {
          filter = FilterEnum.Users;
        }
      });
    }

    const offset = (pageNumber - 1) * limit;
    const queryParams = {
      ...searchParams,
      filter: normalizeFilter(filter),
      types: normalizeTypes(searchParams.types),
      searchFields: normalizeSearchFields(searchParams.searchFields),
      folderId,
      limit,
      offset,
    };

    const includeActionBoards = isInsightBoardAvailable();
    queryParams.types ||= [
      SeeqNames.Types.Folder,
      SeeqNames.Types.Analysis,
      SeeqNames.Types.Topic,
      SeeqNames.Types.Project,
      includeActionBoards ? SeeqNames.Types.ActionBoard : undefined,
      hasValidFeatureInLicense(sqLicenseManagementStore.getState().license, Feature.Vantage) && isVantageEnabled()
        ? SeeqNames.Types.Vantage
        : undefined,
    ].filter(Boolean) as string[];
    cancelGroup(cancellationGroup);
    const { data } = await sqFoldersApi.getFolders(queryParams, { cancellationGroup });

    return _.pick(data, ['content', 'totalResults', 'path']);
  };

  const getNavigateFolders = async (limit: number, pageNumber: number) => {
    const offset = (pageNumber - 1) * limit;
    const includeActionBoards = isInsightBoardAvailable();
    const types =
      normalizeTypes(searchParams.types) ||
      ([
        SeeqNames.Types.Folder,
        SeeqNames.Types.Analysis,
        SeeqNames.Types.Topic,
        SeeqNames.Types.Project,
        includeActionBoards ? SeeqNames.Types.ActionBoard : undefined,
        hasValidFeatureInLicense(sqLicenseManagementStore.getState().license, Feature.Vantage) && isVantageEnabled()
          ? SeeqNames.Types.Vantage
          : undefined,
      ].filter(Boolean) as string[]);

    const isTabTable = table === HOME_SCREEN_TABLE_TYPE.TAB;
    const queryParams = {
      isArchived: Boolean(searchParams.isArchived),
      sortOrder: searchParams.sortOrder,
      offset,
      limit,
      onlyPinned: Boolean(searchParams.onlyPinned),
      folderId: isTabTable ? folderId || undefined : undefined,
      root: isTabTable ? getFolderRoot(currentTab ?? sqHomeScreenStore.currentTab) : undefined,
      types,
    };

    cancelGroup(cancellationGroup);
    const { data } = await sqFoldersApi.navigateFolders(queryParams, { cancellationGroup });
    return _.pick(data, ['content', 'totalResults', 'path']);
  };

  const executeMeilisearch = async (limit: number, pageNumber: number) => {
    const result = await getMeilisearchResults({
      params: searchParams,
      config: {
        ...DEFAULT_SEARCH_CONFIG,
        resultsPerPage: limit,
        page: pageNumber,
      },
      cancellationGroup,
    });

    if (result.error) {
      return { content: [], totalResults: 0 };
    } else {
      return {
        content: result.results,
        path: result.path,
        totalResults: result.totalResults,
      };
    }
  };

  const endpoint = selectEndpoint(table, searchParams);
  const getSearchResults =
    endpoint === 'meili' ? executeMeilisearch : endpoint === 'folders' ? getFolders : getNavigateFolders;

  return getSearchResults(limit, currentPageNumber)
    .then((data) => {
      if (_.isEmpty(data.content) && currentPageNumber > 1) {
        // This can happen when items were deleted from the current page, and there are no more items to display
        // for it.  We simply re-fetch by setting the page to the previous one
        setPageNumber(currentPageNumber - 1, table);
        return getSearchResults(limit, currentPageNumber - 2);
      }
      return data;
    })
    .then((data) => {
      const normalizedContent = Array.isArray(data.content)
        ? data.content
        : data.content
          ? _.castArray(data.content)
          : [];

      flux.dispatch('SET_HOME_SCREEN_ITEMS_FOR_TABLE', {
        table,
        items: normalizedContent as unknown as HomeScreenItemType[],
        totalResults: data?.totalResults,
      });
      if (data?.path) {
        flux.dispatch('SET_BREADCRUMBS', { crumbs: data?.path });
      } else {
        clearBreadcrumbs();
      }
    })
    .finally(() => {
      flux.dispatch('SET_HOME_SCREEN_TABLE_LOADING', { table });
      flux.dispatch('SET_IS_LOADING_BREADCRUMBS', { isLoading: false });
    });
}

export function loadFolderTree(folderId: string | null | undefined, currentTab: string | null) {
  let tabFolderPromise;
  let expandToFolder = false;

  // Check to see if folder is actually a tab,then use it
  tabFolderPromise = getTabFolderName(folderId);

  return tabFolderPromise.then((tabFolder): any => {
    const newTab = _.isUndefined(tabFolder) ? currentTab : tabFolder;
    const folderRoot = getFolderRoot(newTab);
    let promise;

    if (folderId && _.isUndefined(tabFolder)) {
      expandToFolder = true;
      promise = sqFoldersApi.getAncestors(
        { folderId, root: folderRoot },
        { cancellationGroup: HOME_SCREEN_CANCELLATION_GROUP },
      );
    } else if (folderRoot) {
      promise = getNavigationSubfolders(
        { folderId: folderRoot },
        { cancellationGroup: HOME_SCREEN_CANCELLATION_GROUP },
      );
      if (newTab !== sqHomeScreenStore.currentTab) {
        exposedForTesting.clearItemsHomescreen();
        exposedForTesting.setCurrentTab(newTab as string);
      }
    } else {
      return Promise.resolve({});
    }
    return promise.then(({ data: { id, subfolders } }) => {
      mergeSubfolderTree(subfolders ?? []);

      if (expandToFolder) {
        if (!folderRoot && id) {
          if (id === SEARCH_ITEM_LOCATIONS.MY_FOLDER) {
            setCurrentTab(HOME_SCREEN_TABS.MY_FOLDER);
          } else if (id === SEARCH_ITEM_LOCATIONS.SHARED_OR_PUBLIC) {
            if (currentTab !== HOME_SCREEN_TABS.SHARED) {
              setCurrentTab(HOME_SCREEN_TABS.SHARED);
              // CRAB-29112: The SHARED folder has special logic regarding how items are shown users so we must load
              // the folder with the `currentTab` correctly set
              exposedForTesting.clearItemsHomescreen();
              return exposedForTesting
                .loadFolder(folderId, HOME_SCREEN_TABS.SHARED)
                .then(() => exposedForTesting.addExpandedFolderIds(_.map(sqHomeScreenStore.breadcrumbs, 'id')));
            }
          } else if (id === SEARCH_ITEM_LOCATIONS.CORPORATE) {
            setCurrentTab(HOME_SCREEN_TABS.CORPORATE);
          } else if (id === SEARCH_ITEM_LOCATIONS.USERS) {
            setCurrentTab(HOME_SCREEN_TABS.USERS);
          } else if (id === SEARCH_ITEM_LOCATIONS.EXTERNALLY_SHARED) {
            setCurrentTab(HOME_SCREEN_TABS.EXTERNALLY_SHARED);
          }
        }
      } else if (folderRoot) {
        // Set the tab folder as the current folder for navigable folder roots (not filter-based tab roots).
        if (folderRoot === SEARCH_ITEM_LOCATIONS.MY_FOLDER) {
          getTabFolder(HOME_SCREEN_TABS.MY_FOLDER).then((folder: { id: string }) => {
            exposedForTesting.setCurrentFolder(folder.id);
          });
        } else if (folderRoot === SEARCH_ITEM_LOCATIONS.CORPORATE) {
          getTabFolder(HOME_SCREEN_TABS.CORPORATE).then((folder: { id: string }) => {
            exposedForTesting.setCurrentFolder(folder.id);
          });
        } else if (folderRoot === SEARCH_ITEM_LOCATIONS.EXTERNALLY_SHARED) {
          getTabFolder(HOME_SCREEN_TABS.EXTERNALLY_SHARED).then((folder: { id: string } | undefined) => {
            if (folder) {
              exposedForTesting.setCurrentFolder(folder.id);
            }
          });
        }
      }
    });
  });
}

export function loadHomeTab() {
  return Promise.all([loadPinnedTable(), loadRecentTable()]);
}

export function loadTab(currentTab: string, folderId: string | null | undefined) {
  const finalizeTabLoad = (resolvedTab: string) => {
    addExpandedFolderIds(_.map(sqHomeScreenStore.breadcrumbs, 'id'));
    const tabRootFolderId = sqHomeScreenStore.tabFolders?.[resolvedTab ?? '']?.id ?? null;
    const requestedFolderId = folderId ?? null;
    const isTabRootFolder =
      requestedFolderId !== null && tabRootFolderId !== null && equalsIgnoreCase(requestedFolderId, tabRootFolderId);

    if (requestedFolderId !== null) {
      const root = getFolderRoot(resolvedTab);
      getNavigationSubfolders({ folderId: requestedFolderId, root }).then((response) => {
        const folder = response.data;
        const isCurrentFolderNode = folder?.id && equalsIgnoreCase(folder.id, requestedFolderId);

        if (isCurrentFolderNode && isTabRootFolder) {
          // root already represented by top-level children
          replaceFolderChildren(folder.id, folder.subfolders ?? []);
          return;
        }

        if (isCurrentFolderNode) {
          replaceFolderChildren(folder.id, folder.subfolders ?? []);
          return;
        }

        mergeSubfolderTree([folder]);
      });
    }
  };

  // Direct folder URL navigation starts with HOME as currentTab. Resolve the actual tab first so tab-table loading
  // sends the correct root for breadcrumb routing.
  if (currentTab === HOME_SCREEN_TABS.HOME && folderId) {
    return exposedForTesting.loadFolderTree(folderId, currentTab).then(() => {
      const resolvedTab = sqHomeScreenStore.currentTab;
      return exposedForTesting
        .loadTabTable({ currentTab: resolvedTab as HOME_SCREEN_TABS, folderId })
        .then(() => finalizeTabLoad(resolvedTab));
    });
  }

  return Promise.all([
    exposedForTesting.loadTabTable({ currentTab: currentTab as HOME_SCREEN_TABS, folderId }),
    exposedForTesting.loadFolderTree(folderId, currentTab),
  ]).then(() => finalizeTabLoad(currentTab));
}

export function getSearchParams({
  currentTab,
  folderId,
  sortProperty,
  sortAsc,
}: {
  currentTab: HOME_SCREEN_TABS;
  folderId: string;
  sortProperty?: string;
  sortAsc?: boolean;
}): HomeScreenSearchParams {
  const searchParams: HomeScreenSearchParams = {};
  if (sortProperty) {
    searchParams.sortOrder = `${sortProperty} ${(sortAsc ?? true) ? 'asc' : 'desc'}`;
  }
  if (folderId) {
    let usersFilter = currentTab === HOME_SCREEN_TABS.USERS ? SEARCH_ITEM_LOCATIONS.USERS : undefined;
    let sharedFilter = currentTab === HOME_SCREEN_TABS.SHARED ? SEARCH_ITEM_LOCATIONS.SHARED_OR_PUBLIC : undefined;
    return _.assign(searchParams, {
      folderId,
      filter: sharedFilter ?? usersFilter,
    });
  } else {
    switch (currentTab) {
      case HOME_SCREEN_TABS.USERS:
        return _.assign(searchParams, {
          folderId: SEARCH_ITEM_LOCATIONS.USERS,
        });
      case HOME_SCREEN_TABS.MY_FOLDER:
        return _.assign(searchParams, {
          folderId: SEARCH_ITEM_LOCATIONS.MY_FOLDER,
        });
      case HOME_SCREEN_TABS.SHARED:
        return _.assign(searchParams, {
          filter: SEARCH_ITEM_LOCATIONS.SHARED_OR_PUBLIC,
        });
      case HOME_SCREEN_TABS.CORPORATE:
        return _.assign(searchParams, {
          folderId: SEARCH_ITEM_LOCATIONS.CORPORATE,
        });
      case HOME_SCREEN_TABS.EXTERNALLY_SHARED:
        return _.assign(searchParams, {
          folderId: SEARCH_ITEM_LOCATIONS.EXTERNALLY_SHARED,
        });
      case HOME_SCREEN_TABS.TRASH:
        return _.assign(searchParams, { isArchived: true });
      default:
        return searchParams;
    }
  }
}

export function loadFolder(
  folderId?: string | null,
  currentTab?: string,
  options?: { forceClear?: boolean },
): Promise<{ content?: HomeScreenItemType[] }> {
  cancelAll();
  currentTab = currentTab || HOME_SCREEN_TABS.HOME;
  const shouldClear = options?.forceClear === true || currentTab !== sqHomeScreenStore.currentTab;

  if (shouldClear) {
    clearItemsHomescreen();
  }

  // the API returns a magic string for the "root" tab of an item, so that we can show the selected tab without
  // encoding it in the URL.
  // Items can appear in more than one "tab". For example: my folder A is My Folder, but it's also under my home
  // folder in the Users tab.
  // Tabs are not persisted in the store between reloads - so if we have no currentTab we use the one the API
  // returns, if we have a tab we use that, as to avoid a jumpy tab selection.
  if (
    currentTab !== sqHomeScreenStore.currentTab ||
    (!(_.isNil(folderId) && _.isNil(sqHomeScreenStore.currentFolderId)) &&
      !equalsIgnoreCase(folderId, sqHomeScreenStore.currentFolderId))
  ) {
    exposedForTesting.setCurrentTab(currentTab);
    exposedForTesting.setCurrentFolder(folderId);
  }

  if (!folderId && currentTab === HOME_SCREEN_TABS.HOME) {
    return loadHomeTab() as Promise<any>;
  } else {
    return loadTab(currentTab, folderId) as Promise<any>;
  }
}

export function collapseFolder(folderId: string) {
  removeExpandedFolderIds([folderId]);
}

export function getNextTableSort(
  sortProperty: string,
  _table: string,
  tableSort?: SortByTableItem,
): { sortProperty: string; sortAsc: boolean } {
  const sortAsc = tableSort?.sortProperty === sortProperty ? !tableSort?.sortAsc : true;
  return { sortProperty, sortAsc };
}

export function getSortedTableContents(sortProperty: string, table: string) {
  const tableSort = sqHomeScreenStore.getSortForTable(table);
  const { sortProperty: nextSortProperty, sortAsc } = getNextTableSort(sortProperty, table, tableSort);
  setPageNumber(1, table);

  flux.dispatch('SET_HOME_SCREEN_TABLE_SORT', { sortProperty: nextSortProperty, sortAsc, table }, PUSH_WORKBENCH);

  switch (table) {
    case HOME_SCREEN_TABLE_TYPE.PINNED:
      return loadPinnedTable();
    case HOME_SCREEN_TABLE_TYPE.RECENT:
      return loadRecentTable();
    case HOME_SCREEN_TABLE_TYPE.SEARCH:
      return loadSearchTable({});
    default:
      return loadTabTable();
  }
}

// open a folder navigation tree but don't load the folder contents
export function expandFolder(folderId: string, expandedFolderIds: string[], folderRoot: string | null = null) {
  addExpandedFolderIds(_.uniq([...expandedFolderIds, folderId]));
  getNavigationSubfolders({ folderId, root: folderRoot ?? undefined }).then((response) => {
    exposedForTesting.mergeSubfolderTree([response.data]);
  });
}

export function resetStore() {
  flux.dispatch('RESET_HOME_SCREEN_STORE');
}

export function clearSearchResults() {
  flux.dispatch('SET_HOME_SCREEN_ITEMS_FOR_TABLE', {
    table: HOME_SCREEN_TABLE_TYPE.SEARCH,
    items: [],
  });
}

export function addFolder({
  parentFolderId,
  branchFrom,
  name,
  ownerId,
}: {
  parentFolderId?: string;
  branchFrom?: string;
  name?: string;
  ownerId?: string;
}) {
  const folderName = name ? name : getDefaultFolderName();

  return exposedForTesting.canCreateItemInFolder(parentFolderId).then((folderWriteAccess) => {
    setTableSort(HOME_SCREEN_SORT.CREATED_AT, false, HOME_SCREEN_TABLE_TYPE.TAB);
    return createFolder(
      folderName,
      folderWriteAccess && parentFolderId ? parentFolderId : null,
      branchFrom,
      ownerId,
    ).then((folder) => {
      exposedForTesting.setCurrentFolder(folder.parentFolderId);
      return folder;
    });
  });
}

export function addWorkbook({
  name = '',
  addNewWorksheet = true,
  isTopic = false,
  isVantage = false,
  isActionBoard = false,
  branchFrom,
  folderId,
}: {
  name?: string;
  addNewWorksheet?: boolean;
  isTopic?: boolean;
  isVantage?: boolean;
  isActionBoard?: boolean;
  branchFrom?: string;
  folderId?: string | null;
}) {
  const prefix = i18next.t(
    `ITEM_TYPES.${(isTopic && 'REPORT') || (isVantage && 'VANTAGE') || (isActionBoard && 'ACTIONBOARD') || 'WORKBOOK'}`,
  );
  const worksheetPrefix =
    (isTopic && i18next.t('ITEM_TYPES.DOCUMENT')) ||
    (isVantage && i18next.t('ITEM_TYPES.VIEW')) ||
    (isActionBoard && i18next.t('ITEM_TYPES.BOARD')) ||
    '';
  moment.locale(_.truncate(sqWorkbenchStore.userLanguage, { length: 2, omission: '' }));
  const workbookTimeFormat = getPreferredTimeFormat(sqWorkbenchStore.use24HourFormat, 'lll');
  name = name || `${prefix} ${formatTime(new Date(), sqWorksheetStore.timezone, workbookTimeFormat)}`;

  return exposedForTesting
    .canCreateItemInFolder(folderId)
    .then((folderWriteAccess) =>
      createWorkbook(
        name,
        branchFrom,
        folderWriteAccess ? folderId : null,
        (isTopic && 'Topic') || (isVantage && 'Vantage') || (isActionBoard && 'ActionBoard') || 'Analysis',
      ),
    )
    .then((workbook: any) => {
      if (addNewWorksheet && !isSystemTest()) {
        setJournalToOpen(null);
      }
      if (addNewWorksheet && !branchFrom) {
        if (sqWorkbookStore.workbookId !== workbook.workbookId) {
          return loadWorkbook(workbook.workbookId, WORKBOOK_DISPLAY.EDIT).then(() =>
            addWorksheetToWorkbook(workbook.workbookId, {
              prefix: worksheetPrefix,
            }),
          );
        } else {
          return addWorksheetToWorkbook(workbook.workbookId, {
            prefix: worksheetPrefix,
          });
        }
      } else if (branchFrom) {
        return getWorkbook(workbook.workbookId);
      } else {
        return workbook;
      }
    })
    .then((workbook) => _.tap(workbook, emitWorkbooks));
}

export function addProject({ folderId = null, name = null }: { folderId?: string | null; name?: string | null } = {}) {
  const prefix = i18next.t('ITEM_TYPES.PROJECT');
  const projectTimeFormat = getPreferredTimeFormat(sqWorkbenchStore.use24HourFormat, 'lll');
  const projectName = name || `${prefix} ${formatTime(new Date(), sqWorksheetStore.timezone, projectTimeFormat)}`;

  return exposedForTesting
    .canCreateItemInFolder(folderId ?? undefined)
    .then((folderWriteAccess) => createProject(projectName, folderWriteAccess ? folderId : null))
    .then((project) => {
      flux.dispatch('WORKBENCH_ADD_PROJECT', project);
      emitWorkbooks();
      return project;
    });
}

export function addWorksheetToWorkbook(workbookId: string, options?: { prefix?: string }) {
  return addWorksheetToWorkbookAction(workbookId, options).then((worksheet) => {
    // addWorksheet sets the worksheet into the store and this makes the object immutable (freezes the object)
    // to add workbookId on the result we need to work with a clone
    return _.set(_.cloneDeep(worksheet), 'workbookId', workbookId);
  });
}

export function canCreateItemInFolder(folderId?: string | null) {
  const resolvedFolderId = folderId ?? sqHomeScreenStore.currentFolderId;

  if (resolvedFolderId) {
    return getFolder(resolvedFolderId).then((folder) => canModifyWorkbook(folder));
  }

  return Promise.resolve(true);
}

export function removeWorkbook(workbook: { workbookId: string }) {
  return Promise.resolve()
    .then(() => setArchived(workbook.workbookId, true))
    .then((workbook) => _.tap(workbook, emitWorkbooks));
}

export function restoreWorkbook(workbook: { workbookId: string }) {
  return Promise.resolve()
    .then(() => setArchived(workbook.workbookId, false))
    .then((workbook) => _.tap(workbook, emitWorkbooks));
}

export function subscribeToAuthChangeChannel() {
  // Redirect to the logout page if auth changed by another tab or redirect from the login page if authenticated
  subscribeToBroadcastChannel({
    channelId: AUTH_CHANGE_BROADCAST_CHANNEL,
    onMessage() {
      // The csrf is used as a proxy for authentication here if the other tab set the csrf then it is likely
      // that we are authenticated. Similarly, if the csrf has been cleared we are unauthenticated.
      if (isCsrfSet() && _.includes(window.location.pathname, '/login')) {
        returnToPreviousState();
      } else if (!isCsrfSet() && !_.includes(window.location.pathname, '/login')) {
        goTo(getLoginLink(window.location.pathname + window.location.search));
      }
    },
  });
}

export function setTabFolder(key: string, folder: { id: string; name: string }) {
  flux.dispatch('SET_TAB_FOLDER', { key, folder });
}

export function setTrainingPromptDisplay(trainingPromptDisplay: TrainingPromptDisplay) {
  flux.dispatch('SET_HOME_SCREEN_TRAINING_PROMPT_DISPLAY', { trainingPromptDisplay }, PUSH_WORKBENCH);
}

export function setDisplayedAddOnIdentifier(displayedAddOnIdentifier: string) {
  flux.dispatch('SET_HOME_SCREEN_DISPLAYED_ADDON_IDENTIFIER', { displayedAddOnIdentifier }, PUSH_WORKBENCH);
}

export function clearDisplayedAddOnIdentifier() {
  setDisplayedAddOnIdentifier('');
}

export function setCustomLinksExpanded(customLinksExpanded: boolean) {
  flux.dispatch('SET_HOME_SCREEN_CUSTOM_LINKS_EXPANDED', { customLinksExpanded }, PUSH_WORKBENCH);
}

export function setBranchCode(branchCode: string) {
  flux.dispatch('SET_BRANCH_CODE', { branchCode }, PUSH_WORKBENCH);
}

export function setShowOfficeHours(showOfficeHours: boolean) {
  flux.dispatch('SET_SHOW_OFFICE_HOURS', { showOfficeHours }, PUSH_WORKBENCH);
}

export const exposedForTesting = {
  addFolder,
  addProject,
  addWorkbook,
  clearBreadcrumbs,
  clearItemsHomescreen,
  collapseFolder,
  expandFolder,
  loadFolder,
  loadFolderTree,
  loadTabTable,
  mergeSubfolderTree,
  replaceFolderChildren,
  removeWorkbook,
  resetStore,
  restoreWorkbook,
  setCurrentFolder,
  setCurrentTab,
  setPageSizeForTable,
  addWorksheetToWorkbook,
  canCreateItemInFolder,
  addExpandedFolderIds,
  setPageNumber,
  cancelAll,
  loadTable,
  setSearchParams,
};
